<template>
  <component
    :is="tagName"
    class="ak-card__image"
    :class="classes"
    v-bind="options"
  ></component>
</template>

<script>
import { computed, defineComponent } from 'vue'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-card-image :src="'http://example.com/img.png'"></ak-card-image>
   */
  name: 'AkCardImage',

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    /**
     * @type {Object}
     * @prop {Boolean} type - The type of the property
     * @prop {Boolean} default - The default value of the property
     * @description Controls whether we render a div or a img tag, if
     * we render the div, we will also apply the hover-scroll class to
     * ensure that the background image scrolls vertically.
     */
    hoverScroll: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {Object}
     * @prop {String} type - the type of the property
     * @prop {String} default - the default value of the property
     * @description Sets the src of the image tag, or the background url
     * for the required image to be rendered.
     */
    src: {
      type: String,
      default: undefined,
    },

    tag: {
      type: String,
      default: 'img',
    },
  },

  /**
   * @param {Object} props - The properties of the component
   * @description Sets up the component and returns an object containing all of the
   * attributes for the component to use.
   */
  setup(props) {
    const classes = computed(() => {
      const res = []

      if (props.hoverScroll) {
        res.push('ak-card__image--scroll-hover')
      }

      return res
    })

    const tagName = computed(() => {
      if (typeof props.hoverScroll === 'boolean' && props.hoverScroll) {
        return props.tag === 'img' ? 'div' : props.tag
      }
      return props.tag
    })

    const options = computed(() => {
      if (tagName.value !== 'img') {
        const style =
          typeof props.src === 'string'
            ? `background-image: url('${props.src}');background-repeat:no-repeat;background-size:cover;width:100%;`
            : ''
        return {
          style,
        }
      }

      return {
        src: props.src,
      }
    })

    return {
      tagName,
      options,
      classes,
    }
  },
})
</script>
<style lang="scss">
@import '../../scss/_settings.scss';
@import '../../scss/_mixins/_custom';
.ak-card__image {
  position: relative;
  display: block;
  overflow: hidden;
  min-width: 0;
  width: 100%;
  background-size: 100% auto;
  background-position: 0 0;
  object-fit: cover;
  // height: 100%; this should not have a height and should be used with height classes
  &:first-child {
    border-top-left-radius: $base-border-radius;
    border-top-right-radius: $base-border-radius;
    img {
      border-top-left-radius: $base-border-radius;
      border-top-right-radius: $base-border-radius;
    }
  }
  img {
    max-width: 100%;
    height: auto; // Firefox doesnt scale images inside flexbox properly
    width: -moz-available;
    object-fit: cover;
  }
  &--scroll-hover {
    background-repeat: no-repeat;
    transition: background-position 1s ease-out;
    &:hover {
      background-position: 0 100%;
      transition: background-position 5s ease-out;
    }
  }
}
</style>
